import { useCallback, useMemo } from "react";

import { groupBy } from "@/lib/groupBy";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQuery } from "@tanstack/react-query";

import { getLabelListForWorkspaceQueryDocument } from "./label.gql";

export interface UseGetLabelListForWorkspaceProps {
  workspaceId?: string;
  enabled?: boolean;
}

export const useGetLabelListForWorkspace = ({ workspaceId, enabled = true }: UseGetLabelListForWorkspaceProps) => {
  const queryResult = useQuery({
    queryKey: queryKeys.labels.list(workspaceId ?? ""),
    queryFn: async () => {
      if (!workspaceId) {
        throw new Error("Workspace ID is required");
      }

      const client = createOperationClient("GetLabelListForWorkspace");
      const data = await client.request(getLabelListForWorkspaceQueryDocument, {
        input: { workspaceId },
      });

      return data.labelListForWorkspace;
    },
    enabled: enabled && !!workspaceId,
    staleTime: 2 * 60 * 1000, // 2 minutes for list data
  });

  const labelList = useMemo(() => {
    return queryResult.data ?? [];
  }, [queryResult.data]);

  const labelListWithoutGroup = useMemo(() => {
    return labelList.filter((l) => !l.labelGroupId);
  }, [labelList]);

  const labelListGroupedByGroupId = useMemo(() => {
    return groupBy(labelList, (label) => label.labelGroupId);
  }, [labelList]);

  const findLabelById = useCallback(
    (id: string) => {
      return labelList.find((p) => p.id === id);
    },
    [labelList],
  );

  return {
    queryResult,
    labelList,
    labelListWithoutGroup,
    labelListGroupedByGroupId,
    findLabelById,
  };
};
